import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Building2, Users, LogOut, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { BackofficePage } from '../../pages/backoffice/BackofficePage'
import { BackofficeOrgsPage } from '../../pages/backoffice/BackofficeOrgsPage'
import { BackofficeUsersPage } from '../../pages/backoffice/BackofficeUsersPage'

const navItems = [
  { label: 'Dashboard', href: '/backoffice', icon: LayoutDashboard, end: true },
  { label: 'Organizations', href: '/backoffice/organizations', icon: Building2, end: false },
  { label: 'Users', href: '/backoffice/users', icon: Users, end: false },
]

export function BackofficeLayout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="h-screen flex overflow-hidden bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col">
        {/* Brand */}
        <div className="h-16 flex items-center gap-2 px-4 border-b border-slate-800">
          <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="font-semibold text-sm text-slate-100 tracking-wide">Back-office</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map(({ label, href, icon: Icon, end }) => (
            <NavLink
              key={href}
              to={href}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-emerald-600 text-white font-medium'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-800 p-3">
          <div className="flex items-center gap-2 px-2 py-1 mb-2">
            <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {user?.email?.[0]?.toUpperCase() ?? 'A'}
            </div>
            <span className="text-xs text-slate-400 truncate">{user?.email}</span>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition-colors mt-1"
          >
            <LayoutDashboard className="w-4 h-4" />
            Back to app
          </NavLink>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto bg-slate-950">
        <Routes>
          <Route index element={<BackofficePage />} />
          <Route path="organizations" element={<BackofficeOrgsPage />} />
          <Route path="users" element={<BackofficeUsersPage />} />
          <Route path="*" element={<Navigate to="/backoffice" replace />} />
        </Routes>
      </main>
    </div>
  )
}
